import React, { useState } from "react";
import { Dialog, DialogContent } from "../ui/dialog";
import { ZoomIn } from "lucide-react";

/**
 * Lightweight inline markdown renderer: bold + italic + inline code.
 * Intentionally minimal — no HTML injection, no links. Used inside headings/paragraphs/list items.
 */
function Inline({ text }) {
    // Split on **bold**, *italic*, `code` while preserving matched groups.
    const parts = [];
    const re = /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/g;
    let last = 0;
    let m;
    while ((m = re.exec(text)) !== null) {
        if (m.index > last) parts.push(text.slice(last, m.index));
        parts.push(m[0]);
        last = m.index + m[0].length;
    }
    if (last < text.length) parts.push(text.slice(last));

    return (
        <>
            {parts.map((chunk, i) => {
                if (chunk.startsWith("**") && chunk.endsWith("**")) {
                    return <strong key={i} className="text-white font-semibold">{chunk.slice(2, -2)}</strong>;
                }
                if (chunk.startsWith("*") && chunk.endsWith("*") && chunk.length > 2) {
                    return <em key={i}>{chunk.slice(1, -1)}</em>;
                }
                if (chunk.startsWith("`") && chunk.endsWith("`")) {
                    return <code key={i} className="font-mono-tech text-[0.9em] px-1.5 py-0.5 bg-white/5 border border-white/10 rounded">{chunk.slice(1, -1)}</code>;
                }
                return <React.Fragment key={i}>{chunk}</React.Fragment>;
            })}
        </>
    );
}

/**
 * MarkdownBody — minimal Markdown renderer for blog/news/event post bodies.
 * Supports: # / ## / ### headings, **bold**, *italic*, `code`, unordered lists (-, *), paragraphs.
 * Does NOT support: links, images, tables, code blocks, html. Keep it minimal.
 */
// Resolve a backend-relative URL (e.g. /api/uploads/abc) to an absolute URL
function resolveImgSrc(url = "") {
    if (!url) return "";
    if (/^https?:\/\//i.test(url)) return url;
    if (url.startsWith("/api/")) {
        const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
        return `${base}${url}`;
    }
    return url;
}

const IMG_RE = /^!\[([^\]]*)\]\(([^)]+)\)\s*$/;

export default function MarkdownBody({ source = "" }) {
    const [zoomSrc, setZoomSrc] = useState(null);
    const lines = source.replace(/\r\n/g, "\n").split("\n");
    const blocks = [];
    let para = [];
    let list = null;

    const flushPara = () => {
        if (para.length) {
            blocks.push({ type: "p", lines: para });
            para = [];
        }
    };
    const flushList = () => {
        if (list) {
            blocks.push({ type: "ul", items: list });
            list = null;
        }
    };

    for (const raw of lines) {
        const line = raw.trimEnd();
        if (!line.trim()) {
            flushPara();
            flushList();
            continue;
        }
        // Image block: ![alt](url) on its own line → standalone figure
        const img = line.match(IMG_RE);
        if (img) {
            flushPara();
            flushList();
            blocks.push({ type: "img", alt: img[1], src: img[2] });
            continue;
        }
        // figgrid shortcode: ::: figgrid url1 | url2 | caption     (caption optional)
        const grid = line.match(/^:::\s*figgrid\s+(.+)\s*$/i);
        if (grid) {
            flushPara();
            flushList();
            const parts = grid[1].split("|").map((s) => s.trim()).filter(Boolean);
            const captionMaybe = parts.length > 2 ? parts.pop() : "";
            blocks.push({ type: "figgrid", srcs: parts, caption: captionMaybe });
            continue;
        }
        const h = line.match(/^(#{1,3})\s+(.+)$/);
        if (h) {
            flushPara();
            flushList();
            blocks.push({ type: `h${h[1].length}`, text: h[2] });
            continue;
        }
        const li = line.match(/^\s*[-*]\s+(.+)$/);
        if (li) {
            flushPara();
            if (!list) list = [];
            list.push(li[1]);
            continue;
        }
        flushList();
        para.push(line);
    }
    flushPara();
    flushList();

    return (
        <div className="prose-tech space-y-5">
            {blocks.map((b, i) => {
                if (b.type === "h1") {
                    return (
                        <h2 key={i} className="font-serif-display text-3xl md:text-4xl text-white mt-12 mb-4 leading-tight">
                            <Inline text={b.text} />
                        </h2>
                    );
                }
                if (b.type === "h2") {
                    return (
                        <h3 key={i} className="font-serif-display text-2xl md:text-3xl text-white mt-10 mb-3 leading-tight">
                            <Inline text={b.text} />
                        </h3>
                    );
                }
                if (b.type === "h3") {
                    return (
                        <h4 key={i} className="font-mono-tech text-[11px] tracking-[0.22em] uppercase text-[var(--accent-indigo)] mt-8 mb-2">
                            <Inline text={b.text} />
                        </h4>
                    );
                }
                if (b.type === "ul") {
                    return (
                        <ul key={i} className="list-disc pl-6 space-y-1.5 marker:text-[var(--accent-indigo)]/60">
                            {b.items.map((it, j) => (
                                <li key={j}><Inline text={it} /></li>
                            ))}
                        </ul>
                    );
                }
                if (b.type === "img") {
                    const src = resolveImgSrc(b.src);
                    return (
                        <figure key={i} className="my-10">
                            <button
                                type="button"
                                onClick={() => setZoomSrc(src)}
                                className="block w-full relative group cursor-zoom-in"
                                aria-label={`Enlarge: ${b.alt || "image"}`}
                                data-testid="md-figure-zoom"
                            >
                                <img
                                    src={src}
                                    alt={b.alt || ""}
                                    className="w-full frame-corners"
                                    loading="lazy"
                                />
                                <span className="absolute top-3 right-3 inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-[#050510]/85 border border-white/15 text-white/85 text-[10px] font-mono-tech tracking-[0.18em] uppercase opacity-0 group-hover:opacity-100 transition-opacity">
                                    <ZoomIn size={11} /> Enlarge
                                </span>
                            </button>
                            {b.alt ? (
                                <figcaption className="mt-3 font-mono-tech text-[10.5px] tracking-[0.22em] uppercase text-white/50">
                                    {b.alt}
                                </figcaption>
                            ) : null}
                        </figure>
                    );
                }
                if (b.type === "figgrid") {
                    return (
                        <figure key={i} className="my-10">
                            <div className={`grid gap-4 ${b.srcs.length === 2 ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1 md:grid-cols-3"}`}>
                                {b.srcs.map((src) => {
                                    const r = resolveImgSrc(src);
                                    return (
                                        <button
                                            key={src}
                                            type="button"
                                            onClick={() => setZoomSrc(r)}
                                            className="block w-full relative group cursor-zoom-in"
                                            aria-label={`Enlarge: ${b.caption || "image"}`}
                                        >
                                            <img
                                                src={r}
                                                alt={b.caption || ""}
                                                className="w-full frame-corners"
                                                loading="lazy"
                                            />
                                            <span className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-1 bg-[#050510]/85 border border-white/15 text-white/85 text-[9px] font-mono-tech tracking-[0.18em] uppercase opacity-0 group-hover:opacity-100 transition-opacity">
                                                <ZoomIn size={10} /> Enlarge
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                            {b.caption ? (
                                <figcaption className="mt-3 font-mono-tech text-[10.5px] tracking-[0.22em] uppercase text-white/50">
                                    {b.caption}
                                </figcaption>
                            ) : null}
                        </figure>
                    );
                }
                return (
                    <p key={i}>
                        {b.lines.map((l, j) => (
                            <React.Fragment key={j}>
                                {j > 0 && <br />}
                                <Inline text={l} />
                            </React.Fragment>
                        ))}
                    </p>
                );
            })}
            <Dialog open={!!zoomSrc} onOpenChange={(o) => !o && setZoomSrc(null)}>
                <DialogContent
                    className="max-w-[95vw] w-[95vw] max-h-[95vh] p-3 md:p-4 bg-[#05050e] border-white/15"
                    data-testid="md-figure-lightbox"
                >
                    {zoomSrc && (
                        <img
                            src={zoomSrc}
                            alt="Enlarged figure"
                            className="w-full h-auto max-h-[calc(95vh-3rem)] object-contain"
                        />
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
